import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, XCircle, Users, LogIn, CheckCircle, FlaskConical, TrendingUp, Lock, Activity, BarChart2, Shield, Home, Store, Sparkles, Zap } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PublicFooter from '../components/layout/PublicFooter';
import PublicTopNav from '../components/layout/PublicTopNav';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import PerformanceReport from '../components/common/PerformanceReport';
import LeadCaptureModal from '../components/marketplace/LeadCaptureModal';
import DailyInstructions, { shouldShowDailyInstructions } from '../components/marketplace/DailyInstructions';
import { BacktestResponse, MarketplaceListingDetail, MarketplaceSubscription, PerformanceStats } from '../types/marketplace';
import marketplaceService from '../services/marketplaceService';
import { isWhiteLabel, ZEBU_SIGNUP_URL } from '../config/tenantConfig';
import { usePromotion } from '../hooks/usePromotion';
import { CHIP_ACCENT, CHIP_METADATA, CHIP_RISK } from '../utils/chipColors';
import { formatTradingType } from '../utils/scheduleSummary';

const LEAD_MODAL_AUTO_KEY = 'promo_modal_shown';

const wl = isWhiteLabel();
const ACCENT_LINK_HOVER = wl ? 'hover:text-zebu-600 dark:hover:text-zebu-400' : 'hover:text-blue-600 dark:hover:text-blue-400';
const SUBSCRIBED_BADGE = wl
  ? 'bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 border-zebu-200 dark:border-zebu-700'
  : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700';
const LOGIN_BTN = wl
  ? 'border-zebu-600 text-zebu-600 dark:text-zebu-400 dark:border-zebu-500 hover:bg-zebu-50 dark:hover:bg-zebu-900/20'
  : 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20';

// Risk badges — routed through canonical CHIP_RISK (matches MarketplaceBasketCard).
// Pre-Phase-0 used text-{c}-800; canonical CHIP_RISK uses text-{c}-700 (justified
// in chipColors.ts: marketplace cards render at card-list scale, lighter text
// stays scannable). Minor visual shift, no semantic change.
const riskColors: Record<string, string> = {
  LOW:    CHIP_RISK.LOW,
  MEDIUM: CHIP_RISK.MEDIUM,
  HIGH:   CHIP_RISK.HIGH,
};

function formatBacktestPeriod(start?: string, end?: string): string {
  if (!start || !end) return '';
  try {
    const fmt = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
    };
    return `${fmt(start)} – ${fmt(end)}`;
  } catch {
    return '';
  }
}

const categoryStrip: Record<string, string> = {
  INCOME: 'from-emerald-500 to-teal-600',
  MOMENTUM: 'from-blue-500 to-indigo-600',
  HEDGED: 'from-amber-500 to-orange-600',
  AGGRESSIVE: 'from-purple-500 to-pink-600',
  CONSERVATIVE: 'from-emerald-400 to-cyan-500',
};

/** Pulls top stats from backtest and displays as visual highlight cards */
const HighlightCards: React.FC<{ stats: PerformanceStats }> = ({ stats }) => {
  const cards = [
    {
      label: 'CAGR',
      value: `${stats.cagr >= 0 ? '+' : ''}${stats.cagr}%`,
      sub: 'annualised growth rate',
      icon: <BarChart2 className="h-5 w-5" />,
      iconBg: 'bg-blue-100 dark:bg-blue-900/40',
      iconColor: 'text-blue-600 dark:text-blue-400',
      valueColor: stats.cagr >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400',
    },
    {
      label: 'Total Return',
      value: `${stats.totalReturn >= 0 ? '+' : ''}${stats.totalReturn}%`,
      sub: `₹${new Intl.NumberFormat('en-IN').format(Math.abs(stats.netPnl))} net P&L`,
      icon: <TrendingUp className="h-5 w-5" />,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      valueColor: stats.totalReturn >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
    },
    {
      label: 'Win Rate',
      value: `${stats.winRate}%`,
      sub: `${Math.round((stats.winRate / 100) * stats.totalTrades)} / ${stats.totalTrades} trades`,
      icon: <Activity className="h-5 w-5" />,
      iconBg: 'bg-purple-100 dark:bg-purple-900/40',
      iconColor: 'text-purple-600 dark:text-purple-400',
      valueColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      label: 'Sharpe Ratio',
      value: `${stats.sharpeRatio}`,
      sub: 'risk-adjusted return',
      icon: <Shield className="h-5 w-5" />,
      iconBg: 'bg-amber-100 dark:bg-amber-900/40',
      iconColor: 'text-amber-600 dark:text-amber-400',
      valueColor: 'text-amber-600 dark:text-amber-400',
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div key={c.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{c.label}</p>
            <span className={`p-2 rounded-lg ${c.iconBg} ${c.iconColor}`}>{c.icon}</span>
          </div>
          <p className={`text-2xl font-bold ${c.valueColor}`}>{c.value}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">{c.sub}</p>
        </div>
      ))}
    </div>
  );
};

const MarketplaceDetailPage: React.FC = () => {
  const { basketId } = useParams<{ basketId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<MarketplaceListingDetail | null>(null);
  const [backtest, setBacktest] = useState<BacktestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [subscriptions, setSubscriptions] = useState<MarketplaceSubscription[]>([]);
  // Regulatory compliance: backtest numbers are hidden by default. User
  // has to explicitly click through to see historical performance.
  const [showBacktest, setShowBacktest] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Sticky subscribe bar reveal: the bar only appears once the hero CTA has
  // scrolled out of view, so the two CTAs never show at the same time.
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);
  useEffect(() => {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      setIsAuthenticated(!!tokens);
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  // Show the sticky bar only while the hero CTA is off-screen. Re-attaches
  // when the hero renders (after detail loads / auth resolves).
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroCtaVisible(entry.isIntersecting),
      { rootMargin: '0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, error, detail, isAuthenticated]);

  // Lead capture — driven by an admin-configured promotion targeting marketplace-detail.
  const { promotion: detailPromo } = usePromotion('marketplace-detail');
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  useEffect(() => {
    if (!detailPromo || !detailPromo.auto_popup) return;
    const today = new Date().toISOString().slice(0, 10);
    const key = `${LEAD_MODAL_AUTO_KEY}_${detailPromo.promo_id}_${today}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => {
      try { localStorage.setItem(key, '1'); } catch {}
      setLeadModalOpen(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [detailPromo]);

  useEffect(() => {
    if (!basketId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const authenticated = !!localStorage.getItem('auth_tokens');
        const [data, subs, bt] = await Promise.all([
          marketplaceService.getListing(basketId),
          authenticated ? marketplaceService.getSubscriptions().catch(() => []) : Promise.resolve([]),
          marketplaceService.getBacktest(basketId).catch(() => null),
        ]);
        setDetail(data);
        setSubscriptions(subs);
        setBacktest(bt);
      } catch (err: any) {
        setError(err.message || 'Failed to load basket details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [basketId, refreshKey]);

  const isAlreadySubscribed = subscriptions.some(
    (s) => s.marketplace_basket_id === basketId && s.status !== 'CANCELLED'
  );

  // Lazy-import SubscribeDialog only when needed
  const SubscribeDialog = React.useMemo(() => {
    try {
      return React.lazy(() => import('../components/marketplace/SubscribeDialog'));
    } catch {
      return null;
    }
  }, []);

  const content = (
    <div className="space-y-4">
      {!isAuthenticated && (
        <Link
          to="/marketplace"
          className={`inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 ${ACCENT_LINK_HOVER} transition-colors`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Marketplace
        </Link>
      )}

      {loading && <LoadingSpinner message="Loading basket..." />}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {!loading && !error && detail && (
        <>
          {/* Hero header card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            {/* Category gradient strip */}
            <div className={`h-2 bg-gradient-to-r ${categoryStrip[detail.category] || categoryStrip.INCOME}`} />
            <div className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="space-y-3 flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                    {detail.title}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl leading-relaxed">
                    {detail.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CHIP_METADATA.DEFAULT}`}>
                      {detail.category}
                    </span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${riskColors[detail.risk_level] || riskColors.MEDIUM}`}>
                      {detail.risk_level} RISK
                    </span>
                    {/* Trading-type chip — uses CHIP_ACCENT.TRADING_TYPE (cyan)
                        to match StrategyRowView's trading-type chip. Pre-Phase-0
                        this rendered blue (text-blue-800); canonical cyan brings
                        cross-app consistency. Label collapses POSITIONAL
                        strategies with entry_tdbe == exit_tdbe to "INTRADAY"
                        (same-day-on-target-DTE behaviour) and INTRADAY +
                        NEXT_DAY_BTST to "BTST/STBT" via `formatTradingType`. */}
                    {(() => {
                      const strat = detail.strategies?.[0];
                      const label = formatTradingType(
                        strat?.trading_type,
                        strat?.intraday_exit_mode,
                        strat?.entry_trading_days_before_expiry,
                        strat?.exit_trading_days_before_expiry,
                      ) || 'INTRADAY';
                      return (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${CHIP_ACCENT.TRADING_TYPE}`}>
                          {label}
                        </span>
                      );
                    })()}
                    <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Users className="h-3.5 w-3.5" />
                      {detail.subscriber_count} subscribers
                    </span>
                  </div>
                </div>

                <div ref={heroCtaRef} className="shrink-0 flex flex-col items-end gap-2">
                  {isAuthenticated ? (
                    isAlreadySubscribed ? (
                      <div className="flex flex-col items-end gap-1">
                        <span className={`inline-flex items-center gap-2 h-10 px-5 rounded-lg text-sm font-semibold border ${SUBSCRIBED_BADGE}`}>
                          <CheckCircle className="h-4 w-4" />
                          Subscribed
                        </span>
                        <button
                          type="button"
                          onClick={() => navigate('/subscriptions')}
                          className={`text-xs font-medium ${wl ? 'text-zebu-600 dark:text-zebu-400 hover:text-zebu-700' : 'text-blue-600 dark:text-blue-400 hover:text-blue-700'}`}
                        >
                          Manage subscription
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowSubscribe(true)}
                        className={`inline-flex items-center gap-2 h-10 px-6 rounded-lg text-white text-sm font-semibold shadow-sm transition-colors ${wl ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                      >
                        <Zap className="h-4 w-4" />
                        Subscribe
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/auth')}
                      className={`inline-flex items-center gap-2 h-10 px-5 rounded-lg border text-sm font-semibold transition-colors ${LOGIN_BTN}`}
                    >
                      <LogIn className="h-4 w-4" />
                      Login to Subscribe
                    </button>
                  )}
                  {detail.tags && detail.tags.length > 0 && (
                    <div className="flex flex-wrap justify-end gap-1 mt-1">
                      {detail.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Backtest — gated behind an explicit "Backtest" button so the
              historical numbers are never shown by default. Data is already
              fetched; the gate is purely presentational. */}
          {backtest ? (
            showBacktest ? (
              <>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Backtest Results
                    </p>
                    {detail.capital_required && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Based on a capital of{' '}
                        <span className="font-semibold text-gray-700 dark:text-gray-300">
                          ₹{detail.capital_required.toLocaleString('en-IN')}
                        </span>
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBacktest(false)}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    Hide
                  </button>
                </div>
                <HighlightCards stats={backtest.stats} />
                <PerformanceReport
                  title="Backtest Results"
                  icon={<FlaskConical className="h-5 w-5" />}
                  stats={backtest.stats}
                  period={formatBacktestPeriod(backtest.backtest_start_date, backtest.backtest_end_date)}
                  accentColor="emerald"
                />
              </>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-5 py-8 flex flex-col items-center text-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Backtest Results Available</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs mt-1">
                    Historical performance is available for this basket. Click to view CAGR, returns, win rate, drawdown and more.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBacktest(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                >
                  <FlaskConical className="h-3.5 w-3.5" />
                  Backtest
                </button>
              </div>
            )
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-5 py-10 flex flex-col items-center text-center gap-2">
              <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Backtest Results</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
                The strategy provider has not published backtest data yet.
              </p>
            </div>
          )}

          {/* Live Results — coming soon */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-5 py-10 flex flex-col items-center text-center gap-2">
            <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              {isAlreadySubscribed ? <TrendingUp className="h-5 w-5 text-gray-400" /> : <Lock className="h-5 w-5 text-gray-400" />}
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Live Results</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
              {isAlreadySubscribed
                ? 'Live performance tracking for subscribed baskets is coming soon.'
                : isAuthenticated
                  ? 'Subscribe to this basket to unlock live trading results and performance tracking.'
                  : 'Create a free account and subscribe to unlock live trading results for this basket.'}
            </p>
            {!isAuthenticated && (
              <a
                href={ZEBU_SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-zebu-600 hover:bg-zebu-700 text-white transition-colors"
              >
                <LogIn className="h-3.5 w-3.5" />
                Open Demat Account
              </a>
            )}
          </div>

          {/* Subscribe Dialog */}
          {showSubscribe && SubscribeDialog && (
            <React.Suspense fallback={null}>
              <SubscribeDialog
                listing={detail}
                onClose={() => setShowSubscribe(false)}
                onSuccess={() => {
                  setShowSubscribe(false);
                  if (shouldShowDailyInstructions()) setShowInstructions(true);
                  marketplaceService.getSubscriptions().then(setSubscriptions).catch(() => {});
                }}
              />
            </React.Suspense>
          )}

          {/* One-time daily-operating-instructions modal after a successful subscribe. */}
          <DailyInstructions
            variant="modal"
            isOpen={showInstructions}
            onClose={() => setShowInstructions(false)}
          />

          {/* Sticky subscribe bar — keeps the primary CTA reachable after the
              hero scrolls out of view. `position: sticky` floats it over the
              performance content while scrolling, then parks at the end of the
              content (above the footer/marketing CTA) once fully scrolled.
              Hidden for already-subscribed users. */}
          {!isAlreadySubscribed && !heroCtaVisible && (
            <div className="sticky bottom-4 z-30 pointer-events-none">
              <div className="pointer-events-auto flex items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur shadow-lg px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[0.65rem] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {detail.capital_required ? 'Min Capital' : 'Strategy'}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                    {detail.capital_required
                      ? `₹${detail.capital_required.toLocaleString('en-IN')}`
                      : detail.title}
                  </p>
                </div>
                {isAuthenticated ? (
                  <button
                    type="button"
                    onClick={() => setShowSubscribe(true)}
                    className={`shrink-0 inline-flex items-center gap-2 h-10 px-6 rounded-lg text-white text-sm font-semibold shadow-sm transition-colors ${wl ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    <Zap className="h-4 w-4" />
                    Subscribe
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/auth')}
                    className={`shrink-0 inline-flex items-center gap-2 h-10 px-6 rounded-lg text-white text-sm font-semibold shadow-sm transition-colors ${wl ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    <LogIn className="h-4 w-4" />
                    Login to Subscribe
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isAuthenticated) {
    return (
      <StandardLayout>
        <PageHeader
          title={detail?.title || 'Basket Detail'}
          pageType="management"
          icon={<Store className="w-5 h-5" />}
          breadcrumbs={[
            { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
            { label: 'Marketplace' },
            { label: 'Browse Strategies', href: '/marketplace' },
          ]}
          actions={
            <div className="flex flex-wrap gap-2">
              {detailPromo && (
                <Button
                  variant="primary"
                  onClick={() => setLeadModalOpen(true)}
                  leftIcon={<Sparkles className="h-4 w-4" />}
                  className="w-full sm:w-auto"
                >
                  Register Interest
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setRefreshKey((k) => k + 1)}
                leftIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
                className="w-full sm:w-auto"
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          }
        />
        {content}
        {detailPromo && (
          <LeadCaptureModal
            open={leadModalOpen}
            onClose={() => setLeadModalOpen(false)}
            promotion={detailPromo}
            source="marketplace-detail"
            basketId={basketId}
          />
        )}
      </StandardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <PublicTopNav />

      {detailPromo && !loading && !error && detail && (
        <div className="bg-gray-50 dark:bg-gray-900 pt-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
            <button
              type="button"
              onClick={() => setLeadModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-zebu-600 text-white font-semibold text-sm hover:bg-zebu-700 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Register Interest
            </button>
          </div>
        </div>
      )}

      <section className="bg-gray-50 dark:bg-gray-900 py-6 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {content}
        </div>
      </section>

      {/* CTA — only shown for unauthenticated users once detail has loaded */}
      {!loading && !error && detail && (
        <section className="bg-zebu-600 dark:bg-zebu-700 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl font-bold text-white mb-2">
              Start trading with <span className="italic">{detail.title}</span>
            </h2>
            <p className="text-zebu-100 text-sm mb-6 max-w-md mx-auto">
              Create a free account, subscribe, and this basket will execute automatically on your trading account.
            </p>
            <a
              href={ZEBU_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 rounded-lg bg-white text-zebu-700 font-semibold hover:bg-zebu-50 transition-colors"
            >
              Open Demat Account
            </a>
          </div>
        </section>
      )}

      <PublicFooter />

      {detailPromo && (
        <LeadCaptureModal
          open={leadModalOpen}
          onClose={() => setLeadModalOpen(false)}
          promotion={detailPromo}
          source="marketplace-detail"
          basketId={basketId}
        />
      )}
    </div>
  );
};

export default MarketplaceDetailPage;
